import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {useBbox, useComponentProps} from 'helpers/hooks/utils';
import StyledEditorMenu from 'components/molecules/Editors/EditorMenu/EditorMenu.styles';
import EditorMenuButtonEditLink from 'components/atoms/Editors/EditorMenuButtonEditLink/EditorMenuButtonEditLink';
import EditorMenuControlsContainer
  from 'components/atoms/Editors/EditorMenuControlsContainer/EditorMenuControlsContainer';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import EditorMenuButtonBold from 'components/atoms/Editors/EditorMenuButtonBold/EditorMenuButtonBold';
import EditorMenuButtonItalic from 'components/atoms/Editors/EditorMenuButtonItalic/EditorMenuButtonItalic';
import EditorMenuButtonStrikethrough
  from 'components/atoms/Editors/EditorMenuButtonStrikethrough/EditorMenuButtonStrikethrough';
import EditorMenuDivider from 'components/atoms/Editors/EditorMenuDivider/EditorMenuDivider';
import EditorMenuButtonBulletedList
  from 'components/atoms/Editors/EditorMenuButtonBulletedList/EditorMenuButtonBulletedList';
import EditorMenuButtonOrderedList
  from 'components/atoms/Editors/EditorMenuButtonOrderedList/EditorMenuButtonOrderedList';
import EditorMenuButtonCodeBlock from 'components/atoms/Editors/EditorMenuButtonCodeBlock/EditorMenuButtonCodeBlock';
import dom from 'helpers/dom';
import EditorMenuButtonIndent from 'components/atoms/Editors/EditorMenuButtonIndent/EditorMenuButtonIndent';
import EditorMenuButtonUnindent from 'components/atoms/Editors/EditorMenuButtonUnindent/EditorMenuButtonUnindent';
import EditorMenuButtonTaskList from 'components/atoms/Editors/EditorMenuButtonTaskList/EditorMenuButtonTaskList';
import EditorMenuButtonHeadingUp from 'components/atoms/Editors/EditorMenuButtonHeadingUp/EditorMenuButtonHeadingUp';
import EditorMenuButtonHeadingDown
  from 'components/atoms/Editors/EditorMenuButtonHeadingDown/EditorMenuButtonHeadingDown';
import Code from '@mui/icons-material/Code';
import EditorMenuButtonAlignLeft from 'components/atoms/Editors/EditorMenuButtonAlignLeft/EditorMenuButtonAlignLeft';
import EditorMenuButtonAlignCenter
  from 'components/atoms/Editors/EditorMenuButtonAlignCenter/EditorMenuButtonAlignCenter';
import EditorMenuButtonAlignRight from 'components/atoms/Editors/EditorMenuButtonAlignRight/EditorMenuButtonAlignRight';
import EditorMenuButtonAlignJustify
  from 'components/atoms/Editors/EditorMenuButtonAlignJustify/EditorMenuButtonAlignJustify';
import EditorMenuButtonFormatting from 'components/atoms/Editors/EditorMenuButtonFormatting/EditorMenuButtonFormatting';
import EditorMenuButtonDialog from 'components/atoms/Editors/EditorMenuButtonDialog/EditorMenuButtonDialog';

const EditorMenu = React.forwardRef((props, ref) => {
  const {
    size,
    anchor,
    variant,
    showDialog,
    onToggle,
    onDialog,
    ...innerProps
  } = useComponentProps(props, 'EditorMenu', {
    variable: ['anchor']
  });

  const formattingRef = useRef(null);

  const bBox = useBbox(() => dom.getParent(formattingRef.current, null, ['EditorMenu']), ['width']);

  const maxItems = Math.floor((bBox?.width ?? 0) / (size === 'small' ? 38 : 40));
  const menuSize = maxItems > 16 ? 'largest' : (
    maxItems >= 14 ? 'large' : (
      maxItems >= 10 ? 'medium' : (
        maxItems > 5 ? 'small' : 'tiny'
      )
    )
  );

  const renderMenuControls = () => {
    if (variant === 'collapsed') {
      return <React.Fragment>
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    variant="collapsed"
                                    tooltipLabel="Show formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    } else if (menuSize === 'tiny') {
      return <React.Fragment>
        <EditorMenuButtonBold />
        <EditorMenuButtonItalic />
        <EditorMenuDivider />
        <EditorMenuButtonEditLink />
        <Box className="EditorMenu-spacer" />
        {showDialog ? <React.Fragment>
          <EditorMenuDivider />
          <EditorMenuButtonDialog onClick={onDialog}/>
        </React.Fragment> : null}
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    tooltipLabel="Hide formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    } else if (menuSize === 'small') {
      return <React.Fragment>
        <EditorMenuButtonBold />
        <EditorMenuButtonItalic />
        <EditorMenuButtonStrikethrough />
        <EditorMenuDivider />
        <EditorMenuButtonEditLink />
        <EditorMenuDivider />
        <EditorMenuButtonBulletedList />
        {showDialog ? <React.Fragment>
          <EditorMenuButtonDialog onClick={onDialog}/>
        </React.Fragment> : null}
        <Box className="EditorMenu-spacer" />
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    tooltipLabel="Hide formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    } else if (menuSize === 'medium') {
      return <React.Fragment>
        <EditorMenuButtonBold />
        <EditorMenuButtonItalic />
        <EditorMenuButtonStrikethrough />
        <EditorMenuDivider />
        <EditorMenuButtonEditLink />
        <EditorMenuDivider />
        <EditorMenuButtonHeadingUp />
        <EditorMenuButtonHeadingDown />
        <EditorMenuDivider />
        <EditorMenuButtonBulletedList />
        <EditorMenuDivider />
        <EditorMenuButtonCodeBlock IconComponent={Code}/>
        {showDialog ? <React.Fragment>
          <EditorMenuDivider />
          <EditorMenuButtonDialog onClick={onDialog}/>
        </React.Fragment> : null}
        <Box className="EditorMenu-spacer" />
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    tooltipLabel="Hide formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    } else if (menuSize === 'large') {
      return <React.Fragment>
        <EditorMenuButtonBold />
        <EditorMenuButtonItalic />
        <EditorMenuButtonStrikethrough />
        <EditorMenuDivider />
        <EditorMenuButtonEditLink />
        <EditorMenuDivider />
        <EditorMenuButtonHeadingUp />
        <EditorMenuButtonHeadingDown />
        <EditorMenuDivider />
        <EditorMenuButtonOrderedList />
        <EditorMenuButtonBulletedList />
        <EditorMenuDivider />
        {dom.isTouchOnly() ? <React.Fragment>
          <EditorMenuButtonIndent />
          <EditorMenuButtonUnindent />
        </React.Fragment> : <React.Fragment>
          <EditorMenuButtonAlignLeft />
          <EditorMenuButtonAlignCenter />
          <EditorMenuButtonAlignRight />
        </React.Fragment>}
        <EditorMenuDivider />
        <EditorMenuButtonCodeBlock IconComponent={Code}/>
        {showDialog ? <React.Fragment>
          <EditorMenuDivider />
          <EditorMenuButtonDialog onClick={onDialog}/>
        </React.Fragment> : null}
        <Box className="EditorMenu-spacer" />
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    tooltipLabel="Hide formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    } else if (menuSize === 'largest') {
      return <React.Fragment>
        <EditorMenuButtonBold />
        <EditorMenuButtonItalic />
        <EditorMenuButtonStrikethrough />
        <EditorMenuDivider />
        <EditorMenuButtonEditLink />
        <EditorMenuDivider />
        <EditorMenuButtonHeadingUp />
        <EditorMenuButtonHeadingDown />
        <EditorMenuDivider />
        <EditorMenuButtonOrderedList />
        <EditorMenuButtonBulletedList />
        <EditorMenuButtonTaskList />
        <EditorMenuDivider />
        <EditorMenuButtonAlignLeft />
        <EditorMenuButtonAlignCenter />
        <EditorMenuButtonAlignRight />
        <EditorMenuButtonAlignJustify />
        <EditorMenuDivider />
        <EditorMenuButtonIndent />
        <EditorMenuButtonUnindent />
        <EditorMenuDivider />
        <EditorMenuButtonCodeBlock IconComponent={Code}/>
        {showDialog ? <React.Fragment>
          <EditorMenuDivider />
          <EditorMenuButtonDialog onClick={onDialog}/>
        </React.Fragment> : null}
        <Box className="EditorMenu-spacer" />
        <EditorMenuButtonFormatting buttonRef={formattingRef}
                                    className="EditorMenu-formatting"
                                    tooltipLabel="Hide formatting"
                                    onClick={onToggle}/>
      </React.Fragment>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledEditorMenu ref={ref} {...innerProps}>
    <EditorMenuControlsContainer className="EditorMenu-controls">
      {renderMenuControls()}
    </EditorMenuControlsContainer>
  </StyledEditorMenu>
});

EditorMenu.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  anchor: PropTypes.string,
  variant: PropTypes.oneOfType([PropTypes.oneOf(['standard', 'collapsed']), PropTypes.string]),
  showDialog: PropTypes.bool,
  onToggle: PropTypes.func,
  onDialog: PropTypes.func
};

export default EditorMenu;
